import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createVNode as _createVNode, resolveComponent as _resolveComponent, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, withCtx as _withCtx, createTextVNode as _createTextVNode, createBlock as _createBlock, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, Fragment as _Fragment } from "vue"
import _imports_0 from '../../assets/back-arrow.svg'
import _imports_1 from '../../assets/arrow-icon.svg'


const _hoisted_1 = /*#__PURE__*/_createElementVNode("div", {
  id: "example",
  style: {"display":"none"}
}, null, -1)
const _hoisted_2 = {
  key: 0,
  class: "center-container"
}
const _hoisted_3 = {
  key: 0,
  class: "button-container"
}
const _hoisted_4 = /*#__PURE__*/_createElementVNode("img", {
  class: "ml-2",
  src: _imports_0,
  alt: "arrow-right"
}, null, -1)
const _hoisted_5 = {
  key: 0,
  class: "ml-2",
  src: _imports_1,
  alt: "arrow-right"
}

import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { useRoute } from "vue-router";
import { VForm, VWindow } from "vuetify/lib/components/index.mjs";
import { useProviderStore } from "@/stores/Provider";
import { useSessionStore } from "@/stores/Session";
import useDocumentStore from "@/stores/Document";
import FsaFormSteps from "@/types/FsaFormSteps";
import Item from "@/types/FsaItem";
import Invoice from "@/types/FsaInvoice";
import VgsForm from "@/types/VgsForm";
import Session from "@/types/Session";
import FsaVariables from "@/types/FsaVariables";
import FsaProvider from "@/types/FsaProvider";
import PatientInformationForm from "@/components/fsa/PatientInformationForm.vue";
import InsuranceInformationForm from "@/components/fsa/InsuranceInformationForm.vue";
import ClaimInformationForm from "@/components/fsa/ClaimInformationForm.vue";
import ProgressBar from "@/components/common/ProgressBar.vue";
import Review from "@/components/fsa/Review.vue";
import FinalReview from "@/components/common/FinalReview.vue";
import { divideItems } from "@/utils/invoices";
import { useBucketStore } from "@/stores/Bucket";
import { base64ToFile, fileToDataUrl } from "@/utils/converters";
import {
  addSignatureToPdf,
  appendImageOrPdf,
  flattenPDFForm,
} from "@/utils/pdf";
import DeliveryType from "@/types/DeliveryType";
import BinkeyLogo from "@/components/common/BinkeyLogo.vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'FsaFlow',
  setup(__props) {

const USE_VGS = process.env.VUE_APP_USE_VGS === "true";
const POSTGRID_SANDBOX = process.env.VUE_APP_POSTGRID_SANDBOX === "true";
const SHOW_CLAIM_PDFS = process.env.VUE_APP_SHOW_CLAIM_PDFS === "true";
const REWIND_URL = process.env.VUE_APP_REWIND_URL as string;

const sessionId = useRoute().params.sessionId as string;
const currentStep = ref<FsaFormSteps>(FsaFormSteps.User);
const shouldShowBackBtn = computed(
  () => currentStep.value != FsaFormSteps.User
);

// Refs to template DOM elements
const vwindow = ref(null);
const userForm = ref(null);
const fsaForm = ref(null);
const claimForm = ref(null);
const finalReviewForm = ref(null);
const sheetWidth = ref(calculateWidth());

function nextStep(): void {
  if (vwindow.value) (vwindow.value as VWindow).group.next();
}
function prevStep(): void {
  if (vwindow.value) (vwindow.value as VWindow).group.prev();
}

// To do: find better way to do this
function getRefToCurrentStep(): VForm | null {
  switch (currentStep.value) {
    case FsaFormSteps.User:
      return userForm.value;
    case FsaFormSteps.FSA:
      return fsaForm.value;
    case FsaFormSteps.Claim:
      return claimForm.value;
    case FsaFormSteps.FinalReview:
      return finalReviewForm.value;
    default:
      return null;
  }
}

async function validateCurrentStep() {
  const form = getRefToCurrentStep();
  return form ? (await form.validate()).valid : true;
}

const shouldShowErrorMessage = ref<boolean>(false);
const errorMessage = ref<string>("");

function showErrorMessage(message: string) {
  errorMessage.value = message;
  shouldShowErrorMessage.value = true;
}

const providers = ref<FsaProvider[]>([]);

// To do: find better way to do this
const selectedProvider = computed(() =>
  providers.value.find(
    (provider) => provider.name === variables.value.planAdministrator
  )
);

const deliveryType = computed(() => selectedProvider.value?.deliveryType);
const faxNumber = computed(
  () => process.env.VUE_APP_PDF_FAX || selectedProvider.value?.faxNumber
);
const postgridContactId = computed(() =>
  POSTGRID_SANDBOX
    ? selectedProvider.value?.postgridContactId?.sandbox
    : selectedProvider.value?.postgridContactId?.live
);

async function onClickNext() {
  const isValid = await validateCurrentStep();

  if (isValid) {
    await saveSession();

    if (currentStep.value == FsaFormSteps.FinalReview) {
      await generateClaimPdf();
      await saveSession();

      if (!SHOW_CLAIM_PDFS) redirectToRewind();
    } else {
      nextStep();
    }
  } else {
    showErrorMessage("Please enter the required fields");
  }
}

function redirectToRewind() {
  window.location.href = `${REWIND_URL}/withdrawal?session-id=${sessionId}`;
}

const variables = ref<FsaVariables>({});
const vgsForm = ref<VgsForm | undefined>(undefined);
const claimPdfs = ref<string[]>([]);
const claimKeys = ref<string[]>([]);
const generatingPdfs = ref<boolean>(false);
const errorGeneratingPdfs = ref<boolean>(false);
const invoices = ref<Invoice[]>([]);
const items = ref<Item[]>([]);
const loadingSession = ref<boolean>(true);
const savingSession = ref<boolean>(false);

const itemCount = computed(() => items.value.length);
const maxItems = computed(() => selectedProvider.value?.itemsPerClaim || 0);

// To do: move to back end
async function getItemsData() {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const itemsData: any = {};
  items.value?.forEach((item: Item, i: number) => {
    const itemNumber = (i % maxItems.value) + 1;
    const claimNumber = Math.floor(i / maxItems.value);

    // To do: find better way to do this
    itemsData[`item${itemNumber}_itemDescription____${claimNumber}`] =
      item.name;

    // To do: save provider or store name when creating session in back end
    itemsData[`item${itemNumber}_serviceProvider____${claimNumber}`] =
      "Provider";
    itemsData[`item${itemNumber}_amount____${claimNumber}`] = item.cost;
    itemsData[`item${itemNumber}_patientName____${claimNumber}`] =
      fullName.value;

    // To do: save both dates when creating session
    itemsData[`item${itemNumber}_startDate____${claimNumber}`] = item.date;
    itemsData[`item${itemNumber}_endDate____${claimNumber}`] = item.date;
  });
  return itemsData;
}

async function loadSession() {
  try {
    const sessionStore = useSessionStore();
    const response = USE_VGS
      ? await sessionStore.getSessionFromVgs(sessionId)
      : await sessionStore.getSession(sessionId);

    const session = response?.data.session;
    // To do: change in back end
    if (session?.subject?.lastName)
      variables.value.lastName = session.subject.lastName;
    if (session?.subject?.firstName)
      variables.value.firstName = session.subject.firstName;
    if (session?.subject?.middleName)
      variables.value.middleInitial = session.subject.middleName;
    if (session?.subject?.state)
      variables.value.addressState = session.subject.state;
    if (session?.subject?.city)
      variables.value.addressCity = session.subject.city;
    if (session?.subject?.zip) variables.value.addressZip = session.subject.zip;
    if (session?.subject?.address)
      variables.value.addressStreet = session.subject.address;
    if (session?.subject?.email) variables.value.email = session.subject.email;
    if (session?.subject?.phone)
      variables.value.phoneNumber = session.subject.phone;

    if (session?.claim) variables.value = session.claim;
    if (session?.step) currentStep.value = session.step;
    if (session?.providerName)
      variables.value.planAdministrator = session.providerName;

    if (session?.invoices != undefined) invoices.value = session.invoices;
    items.value = invoices.value?.reduce((list: Item[], invoice: Invoice) => {
      return list.concat(
        invoice.items.map((item) => ({
          ...item,
          fileUrl: invoice.fileBase64,
          date: invoice.date,
          provider: invoice.merchant.name,
        }))
      );
    }, []);
  } catch {
    console.log("Error loading session");
  } finally {
    loadingSession.value = false;
  }
}

async function saveSession() {
  savingSession.value = true;
  // To do: change in back end
  const itemsData = await getItemsData();

  // To do: change in back end
  const contactIdOrFaxNumber =
    deliveryType.value == DeliveryType.Address
      ? postgridContactId.value
      : faxNumber.value;

  const data = {
    sessionId: sessionId,
    step: currentStep.value,
    signature: signatureDataUrl.value,
    providerName: variables.value.planAdministrator,
    invoices: invoices.value,
    pdfKeys: [...claimKeys.value],
    deliveryType: deliveryType.value, // To do: change depending on provider
    deliveryAddress: contactIdOrFaxNumber,
    variables: {
      ...variables.value, // personal and insurance info
      ...itemsData, // claim info
      fullName: fullName.value,
      accountHolderFullName: accountHolderFullName.value,
      accountHolderPhoneNumber: variables.value.accountHolderPhoneNumber
        ? variables.value.accountHolderPhoneNumber?.replace(/[^\d]/g, "")
        : undefined,
      phoneNumber: variables.value.phoneNumber
        ? variables.value.phoneNumber?.replace(/[^\d]/g, "")
        : undefined,
      hsl_review_answers: true,
    },
  };

  try {
    if (USE_VGS) {
      await submitVgsForm(JSON.parse(JSON.stringify(data)));
    } else {
      await useSessionStore().saveSession(data);
    }
  } catch {
    showErrorMessage("Error saving session information");
  } finally {
    savingSession.value = false;
  }
}

async function submitVgsForm(data: Session) {
  const apiKey = process.env.VUE_APP_BACKEND_API_KEY as string;

  vgsForm.value?.toRaw().submit(
    "/session/update",
    { data, method: "PUT", headers: { "x-api-key": apiKey } },
    (_status: unknown, response: unknown) => {
      console.log("VGS Response: ", response);
    },
    () => {
      console.log("Error saving session info");
    }
  );
}

// To do: Add signature, append receipts to claim Pdf
async function generateClaimPdf() {
  generatingPdfs.value = true;
  const gavelWorkflowName = selectedProvider.value?.workflow || "";

  // Pdfs generated by Gavel, without signature or receipts
  const response = await useDocumentStore().getPDFFSA(
    sessionId,
    Math.floor(itemCount.value / maxItems.value),
    gavelWorkflowName
  );

  const s3BucketStore = useBucketStore();
  const generatedPdfs = response?.data?.pdfs;

  // Each Claim has an array of items, an array of receipt urls, and an array of s3 bucket keys
  const claims = await divideItems(invoices.value, maxItems.value);

  for (let i = 0; i < claims.length; i++) {
    generatedPdfs[i] = await addSignatureToPdf(
      generatedPdfs[i],
      signatureDataUrl.value,
      signatureInfo.value
    );

    // Only append the receipts that contain items in the current claim
    // To do: handle errors
    for (let j = 0; j < claims[i].bucketLinks.length; j++) {
      const receipt = await s3BucketStore.getFile(
        claims[i].bucketLinks[j],
        claims[i].bucketKeys[j],
        "application/pdf"
      );

      if (receipt) {
        const receiptUrl = await fileToDataUrl(receipt);
        generatedPdfs[i] = await appendImageOrPdf(generatedPdfs[i], receiptUrl);
      }
    }

    // Remove input fields
    generatedPdfs[i] = await flattenPDFForm(generatedPdfs[i]);
    const pdfName = sessionId + "-claim-" + i + ".pdf";

    await s3BucketStore
      .saveFile(
        pdfName,
        await base64ToFile(generatedPdfs[i], "application/pdf", pdfName),
        "application/pdf"
      )
      .then(() => {
        claimKeys.value[i] = pdfName;
        errorGeneratingPdfs.value = false;
      })
      .catch(() => {
        // To do: handle errors
        errorGeneratingPdfs.value = true;
        console.log("Error saving claim pdf");
      })
      .finally(() => {
        generatingPdfs.value = false;
      });
  }
  claimPdfs.value = generatedPdfs;
}

const fullName = computed((): string => {
  const { lastName, firstName, middleInitial } = variables.value;
  return `${firstName} ${middleInitial ? `${middleInitial}. ` : ""}${lastName}`;
});

const accountHolderFullName = computed((): string => {
  const lastName = variables.value.accountHolderLastName;
  const firstName = variables.value.accountHolderFirstName;
  const middleInitial = variables.value.accountHolderMiddleInitial;
  return `${firstName} ${middleInitial ? `${middleInitial}. ` : ""}${lastName}`;
});

const signatureDataUrl = ref<string>("");
const signatureInfo = computed(() => selectedProvider.value?.signature);

onMounted(async () => {
  providers.value = (await useProviderStore().listFsaProviders()).data;

  if (USE_VGS) {
    vgsForm.value = new VgsForm();
    vgsForm.value.toRaw().field("#example", { name: "example", type: "text" });
  }
  await loadSession();

  window.addEventListener("resize", handleResize);
});

function calculateWidth() {
  // Check if window is defined
  // Access window.innerWidth directly
  const screenWidth = ref(window.innerWidth);

  if (screenWidth.value <= 500) {
    return "95%";
  } else if (screenWidth.value <= 700) {
    return "85%";
  } else {
    return "60%";
  }
}
onBeforeUnmount(() => {
  // Remove event listener on component unmount
  window.removeEventListener("resize", handleResize);
});

function handleResize() {
  // Handle the resize event here, you can call your method
  sheetWidth.value = calculateWidth();
}

return (_ctx: any,_cache: any) => {
  const _component_v_progress_circular = _resolveComponent("v-progress-circular")!
  const _component_v_window_item = _resolveComponent("v-window-item")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_sheet = _resolveComponent("v-sheet")!
  const _component_v_snackbar = _resolveComponent("v-snackbar")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _hoisted_1,
    _createVNode(BinkeyLogo),
    (loadingSession.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createVNode(_component_v_progress_circular, {
            size: 70,
            width: 7,
            color: "primary",
            indeterminate: ""
          })
        ]))
      : (_openBlock(), _createBlock(_component_v_sheet, {
          key: 1,
          class: "mx-auto my-4",
          width: sheetWidth.value,
          color: "#f0f0f0"
        }, {
          default: _withCtx(() => [
            _createVNode(ProgressBar, {
              modelValue: currentStep.value,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((currentStep).value = $event))
            }, null, 8, ["modelValue"]),
            _createVNode(_component_v_card, { class: "card-container" }, {
              default: _withCtx(() => [
                _createVNode(_unref(VWindow), {
                  class: "pa-5 md:pa-6",
                  ref_key: "vwindow",
                  ref: vwindow,
                  modelValue: currentStep.value,
                  "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((currentStep).value = $event))
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_window_item, {
                      value: _unref(FsaFormSteps).User
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(VForm), {
                          ref_key: "userForm",
                          ref: userForm
                        }, {
                          default: _withCtx(() => [
                            _createVNode(PatientInformationForm, {
                              modelValue: variables.value,
                              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((variables).value = $event))
                            }, null, 8, ["modelValue"])
                          ]),
                          _: 1
                        }, 512)
                      ]),
                      _: 1
                    }, 8, ["value"]),
                    _createVNode(_component_v_window_item, {
                      value: _unref(FsaFormSteps).FSA
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(VForm), {
                          ref_key: "fsaForm",
                          ref: fsaForm
                        }, {
                          default: _withCtx(() => [
                            _createVNode(InsuranceInformationForm, {
                              providers: providers.value,
                              modelValue: variables.value,
                              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((variables).value = $event))
                            }, null, 8, ["providers", "modelValue"])
                          ]),
                          _: 1
                        }, 512)
                      ]),
                      _: 1
                    }, 8, ["value"]),
                    _createVNode(_component_v_window_item, {
                      value: _unref(FsaFormSteps).Claim
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(VForm), {
                          ref_key: "claimForm",
                          ref: claimForm
                        }, {
                          default: _withCtx(() => [
                            _createVNode(ClaimInformationForm, {
                              modelValue: items.value,
                              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((items).value = $event)),
                              "validate-method": validateCurrentStep
                            }, null, 8, ["modelValue"])
                          ]),
                          _: 1
                        }, 512)
                      ]),
                      _: 1
                    }, 8, ["value"]),
                    _createVNode(_component_v_window_item, {
                      value: _unref(FsaFormSteps).Review
                    }, {
                      default: _withCtx(() => [
                        _createVNode(Review, {
                          modelValue: currentStep.value,
                          "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((currentStep).value = $event)),
                          variables: variables.value,
                          items: items.value
                        }, null, 8, ["modelValue", "variables", "items"])
                      ]),
                      _: 1
                    }, 8, ["value"]),
                    _createVNode(_component_v_window_item, {
                      value: _unref(FsaFormSteps).FinalReview
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(VForm), {
                          ref_key: "finalReviewForm",
                          ref: finalReviewForm
                        }, {
                          default: _withCtx(() => [
                            _createVNode(FinalReview, {
                              modelValue: signatureDataUrl.value,
                              "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((signatureDataUrl).value = $event)),
                              claimPdfs: claimPdfs.value,
                              loading: generatingPdfs.value,
                              showPdfs: SHOW_CLAIM_PDFS,
                              errorGeneratingPdfs: errorGeneratingPdfs.value
                            }, null, 8, ["modelValue", "claimPdfs", "loading", "errorGeneratingPdfs"])
                          ]),
                          _: 1
                        }, 512)
                      ]),
                      _: 1
                    }, 8, ["value"])
                  ]),
                  _: 1
                }, 8, ["modelValue"])
              ]),
              _: 1
            }),
            (currentStep.value != _unref(FsaFormSteps).Confirmation)
              ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                  (shouldShowBackBtn.value)
                    ? (_openBlock(), _createBlock(_component_v_btn, {
                        key: 0,
                        class: "binkey-button-back",
                        onClick: prevStep,
                        disabled: savingSession.value || generatingPdfs.value
                      }, {
                        default: _withCtx(() => [
                          _hoisted_4,
                          _createTextVNode(" Back ")
                        ]),
                        _: 1
                      }, 8, ["disabled"]))
                    : _createCommentVNode("", true),
                  _createVNode(_component_v_btn, {
                    class: "binkey-button-next",
                    onClick: onClickNext,
                    color: "primary",
                    disabled: 
            currentStep.value == _unref(FsaFormSteps).InsuranceInformation ||
            savingSession.value ||
            generatingPdfs.value
          
                  }, {
                    default: _withCtx(() => [
                      _createElementVNode("div", {
                        class: _normalizeClass(`next-btn-text-container ${
              currentStep.value == _unref(FsaFormSteps).FinalReview ? 'submit-claim-text' : ''
            }`)
                      }, [
                        _createElementVNode("div", null, _toDisplayString(currentStep.value == _unref(FsaFormSteps).FinalReview
                  ? "Submit Claim"
                  : "Next"), 1),
                        (currentStep.value != _unref(FsaFormSteps).FinalReview)
                          ? (_openBlock(), _createElementBlock("img", _hoisted_5))
                          : _createCommentVNode("", true)
                      ], 2)
                    ]),
                    _: 1
                  }, 8, ["disabled"])
                ]))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }, 8, ["width"])),
    _createVNode(_component_v_snackbar, {
      modelValue: shouldShowErrorMessage.value,
      "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((shouldShowErrorMessage).value = $event)),
      color: "red",
      timeout: "2000"
    }, {
      actions: _withCtx(() => [
        _createVNode(_component_v_btn, {
          color: "white",
          variant: "text",
          onClick: _cache[7] || (_cache[7] = ($event: any) => (shouldShowErrorMessage.value = false))
        }, {
          default: _withCtx(() => [
            _createTextVNode(" X ")
          ]),
          _: 1
        })
      ]),
      default: _withCtx(() => [
        _createTextVNode(_toDisplayString(errorMessage.value) + " ", 1)
      ]),
      _: 1
    }, 8, ["modelValue"])
  ], 64))
}
}

})